import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, EyeIcon, TrashIcon, CheckCircleIcon } from "lucide-react";
import type { RoomBookingDTO } from "../../domain/contracts/room-bookings";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type RoomBookingsTableProps = {
  data: RoomBookingDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (booking: RoomBookingDTO) => void;
  onEdit: (booking: RoomBookingDTO) => void;
  onComplete?: (id: string) => void;
  onCancel: (id: string) => void;
};

export function RoomBookingsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onComplete,
  onCancel,
}: RoomBookingsTableProps) {
  const columns: TanstackReactTable.ColumnDef<RoomBookingDTO>[] = [
    createSortableColumn("date", "ວັນທີ", {
      cell: ({ row }) => formatDate(row.original.date),
    }),
    {
      header: "ເວລາ",
      accessorKey: "startTime",
      cell: ({ row }) => (
        <div>
          {formatTime(row.original.startTime)} -{" "}
          {formatTime(row.original.endTime)}
        </div>
      ),
    },
    {
      header: "ຫ້ອງ",
      accessorKey: "roomName",
      cell: ({ row }) => row.original.roomName,
    },
    {
      header: "ຜູ້ຈອງ / ວັດຖຸປະສົງ",
      accessorKey: "title",
      cell: ({ row }) => (
        <div>
          {row.original.title && (
            <div className="font-medium">{row.original.title}</div>
          )}
          {row.original.contactName && (
            <div className="text-muted-foreground text-xs">
              {row.original.contactName}
              {row.original.contactPhone && ` · ${row.original.contactPhone}`}
            </div>
          )}
          {!row.original.title && !row.original.contactName && "—"}
        </div>
      ),
    },
    {
      header: "ຄ່າເຊົ່າ",
      accessorKey: "amount",
      cell: ({ row }) =>
        row.original.amount != null
          ? `${row.original.amount.toLocaleString()} ກີບ`
          : "—",
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        const variant =
          status === "scheduled"
            ? "default"
            : status === "completed"
              ? "secondary"
              : "outline";
        const label =
          status === "scheduled"
            ? "ຈອງແລ້ວ"
            : status === "completed"
              ? "ສຳເລັດ"
              : "ຍົກເລີກ";
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: RoomBookingDTO } }) => {
        const booking = row.original;
        const canEdit = booking.status === "scheduled";
        const canCancel =
          booking.status === "scheduled" || booking.status === "completed";
        const actions = [];

        actions.push({
          label: "ເບິ່ງລາຍລະອຽດ",
          icon: <EyeIcon className="h-4 w-4" />,
          onClick: () => onView(booking),
        });

        if (canEdit) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(booking),
          });
        }

        if (canEdit && onComplete) {
          actions.push({
            label: "ສຳເລັດ",
            icon: <CheckCircleIcon className="h-4 w-4" />,
            onClick: () => onComplete(booking.id),
          });
        }

        if (canCancel) {
          actions.push({
            label: "ຍົກເລີກ",
            variant: "destructive" as const,
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ຍົກເລີກການຈອງຫ້ອງ",
                description:
                  booking.status === "completed"
                    ? "ການຈອງນີ້ສຳເລັດແລ້ວ — ການຍົກເລີກຈະບໍ່ນັບຄ່າຈ້າງຄູຊ່ວຍສອນ (ຖ້າມີ). ທ່ານແນ່ໃຈບໍ?"
                    : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກການຈອງນີ້?",
                actionText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onCancel(booking.id);
              }
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<RoomBookingDTO, unknown>
      noDataMessage="ບໍ່ພົບການຈອງຫ້ອງ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
